import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { PatientGroups, Column, PatientProfile } from 'utils/models';
import { IndicatorLabelWithTimestamp } from 'components/IndicatorLabelWithTimestamp';
import { GroupLabel } from 'components/GroupLabel';
import { DataFieldNames } from 'pages/Main/Patients/PatientsTable.config';
import { renderGenderLabel } from 'utils/general';
import { GroupSettingsTabs } from 'pages/Settings/Forms/GroupSettings.helpers';
import { PatientDetailsTabs } from 'pages/Main/Patients/Patients.helpers';
import { useOpenHeartRhythmMeasurementDetail } from 'hooks/api/useHeartRhythmData';
import { TableColumnActions } from '../TableBody/TableColumnActions';
import { usePatientListActions } from '../Actions/usePatientsListActions';

export const PatientsTableColumns = (): Column<PatientProfile>[] => {
  const navigate = useNavigate();
  const { mutate: openMeasurementDetail } = useOpenHeartRhythmMeasurementDetail();
  const actions = usePatientListActions();

  const handleGroupLabelClick = (id: string) => {
    window.scrollTo(0, 0);
    navigate(`/settings/${id}/${GroupSettingsTabs.General}`);
  };
  const renderGroupItem = (group: PatientGroups) => (
    <GroupLabel onClick={() => handleGroupLabelClick(group.id)} key={group.id} group={group} />
  );

  return [
    {
      header: t('last_name_label'),
      width: 10,
      renderItem: (item) => <b>{item.lastName}</b>,
      onClick: (item) => navigate(`/patients/${item.id}/details/${PatientDetailsTabs.Overview}`),
      sortFieldName: DataFieldNames.cleanedLastName,
    },
    {
      header: t('first_name_label'),
      width: 10,
      renderItem: (item) => <b>{item.firstName}</b>,
      onClick: (item) => navigate(`/patients/${item.id}/details/${PatientDetailsTabs.Overview}`),
      sortFieldName: DataFieldNames.cleanedFirstName,
    },
    {
      header: t('patients_group_id_label'),
      width: 10,
      renderItem: (item) => <>{item.patientId}</>,
    },
    {
      header: t('gender_label'),
      width: 10,
      renderItem: (item) => <>{renderGenderLabel(item.gender)}</>,
    },
    {
      header: t('date_of_birth_label'),
      width: 10,
      renderItem: (item) => <>{item.birthday}</>,
    },
    {
      header: `${t('group_label')} / ${t('days_left_label')}`,
      width: 22,
      renderItem: (item) => item.groups.map((group) => renderGroupItem(group)),
    },
    {
      header: t('last_measurement_label'),
      width: 15,
      renderItem: (item) => <IndicatorLabelWithTimestamp measurement={item.lastMeasurement} formatWithTime={true} />,
      onClick: (item) => openMeasurementDetail({ patientId: item.id, measurement: item.lastMeasurement }),
      sortFieldName: DataFieldNames.lastMeasurementDate,
    },
    {
      header: t('most_severe_measurement_label'),
      width: 15,
      renderItem: (item) => <IndicatorLabelWithTimestamp measurement={item.mostSevereMeasurement} />,
      onClick: (item) => openMeasurementDetail({ patientId: item.id, measurement: item.mostSevereMeasurement }),
      sortFieldName: DataFieldNames.lastMostSevereMeasurementDate,
    },
    {
      header: t('table_actions'),
      width: 7.5,
      renderItem: (item) => <TableColumnActions<PatientProfile> actions={actions} item={item} />,
    },
  ];
};
